import React, {FC, useEffect, useRef, useState} from "react";
import {AlertCircle, CheckCircle, Filter, Save, Star, Trash2, Upload, X} from "lucide-react";
import {Button} from "@/shared/components/ui/button";
import {Input} from "@/shared/components/ui/input";
import {TooltipProvider} from "@/shared/components/ui/tooltip";
import IconButtonTooltip from "@/shared/components/IconButtonTooltip/IconButtonTooltip";
import type {ReturnTypeUseTablePreset} from "@/shared/hooks/useTablePreset";
import {PresetStorage} from "@/features/types/AutoTablePresetInterface";
import {Toast} from "../Toast";
import {AlertPopup} from "@/shared/components/AlertPopup";

interface FilterPresetButtonProps {
    tablePreset: ReturnTypeUseTablePreset;
}

const PRESET_NAME_MAX_LENGTH = 32;
const PRESET_NAME_REGEX = /^[A-Za-z0-9ąćęłńóśżźĄĆĘŁŃÓŚŻŹ ._-]+$/;

const FilterPresetButton: FC<FilterPresetButtonProps> = ({tablePreset}) => {
    const {
        loadPreset,
        loadAllPresetsFromServer,
        savePresetToServer,
        deletePresetOnServer,
        setDefaultPresetOnServer,
    } = tablePreset;

    const [dropdownOpen, setDropdownOpen] = useState(false);

    const [modalMode, setModalMode] = useState<"save" | "load" | null>(null);
    const [modalOpen, setModalOpen] = useState(false);

    const [storage, setStorage] = useState<PresetStorage>({presets: {}});
    const [presetName, setPresetName] = useState("");

    const dropdownRef = useRef<HTMLDivElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);

    const inputRef = useRef<HTMLInputElement>(null);
    const [confirmAction, setConfirmAction] = useState<{ type: "delete" | "overwrite"; name: string } | null>(null);
    const [validation, setValidation] = useState<{ type: "error" | "success"; message: string } | null>(null);

    const [toast, setToast] = useState<string | null>(null);
    const [toastVisible, setToastVisible] = useState(false);
    const toastTimeout = useRef<NodeJS.Timeout | null>(null);

    const showToast = (msg: string) => {
        setToast(msg);
        setToastVisible(true);

        if (toastTimeout.current) clearTimeout(toastTimeout.current);

        toastTimeout.current = setTimeout(() => {
            setToastVisible(false);
            setTimeout(() => setToast(null), 300);
        }, 2500);
    };

    // Load presets at startup
    useEffect(() => {
        const init = async () => {
            const data = await loadAllPresetsFromServer();
            setStorage(data);

            if (data.default && data.presets[data.default]) {
                loadPreset(data.presets[data.default]);
            }
        };
        init().catch(console.error);
    }, []);

    useEffect(() => {
        if (modalMode === "save" && modalOpen) {
            setTimeout(() => inputRef.current?.focus(), 20);
        }
    }, [modalMode, modalOpen]);

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (!dropdownRef.current?.contains(e.target as Node)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    // Close modal on outside click
    useEffect(() => {
        if (!modalOpen) return;
        const handler = (e: MouseEvent) => {
            if (confirmAction) return;
            if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
                closeModal();
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [modalOpen, confirmAction]);

    const closeModal = () => {
        setModalMode(null);
        setModalOpen(false);
        setPresetName("");
        setConfirmAction(null);
        setValidation(null);
    };

    const openSaveModal = () => {
        setDropdownOpen(false);
        setModalMode("save");
        setModalOpen(true);
    };

    const openLoadModal = () => {
        setDropdownOpen(false);
        setModalMode("load");
        setModalOpen(true);
    };

    const validatePresetName = (name: string): string | null => {
        if (!name) return "Podaj nazwę presetu.";
        if (name.length > PRESET_NAME_MAX_LENGTH) {
            return `Nazwa może mieć maksymalnie ${PRESET_NAME_MAX_LENGTH} znaki.`;
        }
        if (!PRESET_NAME_REGEX.test(name)) {
            return "Dozwolone znaki: litery, cyfry, spacje oraz . _ -";
        }
        return null;
    };

    const savePresetConfirmed = async (name: string, isOverwrite = false) => {
        await savePresetToServer(name);
        const refreshed = await loadAllPresetsFromServer();
        setStorage(refreshed);

        showToast(`${isOverwrite ? "Nadpisano" : "Zapisano"} preset "${name}"`);
        closeModal();
    };

    const handleSavePreset = async () => {
        const name = presetName.trim();
        const validationError = validatePresetName(name);
        if (validationError) {
            setValidation({type: "error", message: validationError});
            return;
        }

        if (storage.presets[name]) {
            setConfirmAction({type: "overwrite", name});
            return;
        }

        await savePresetConfirmed(name);
    };

    const applyPreset = (name: string) => {
        if (confirmAction) return;

        const p = storage.presets[name];
        if (!p) return;

        loadPreset(p);
        showToast(`Wczytano "${name}"`);
        closeModal();
    };

    const handleDeletePresetConfirmed = async (name: string) => {
        await deletePresetOnServer(name);
        const refreshed = await loadAllPresetsFromServer();
        setStorage(refreshed);

        showToast(`Usunięto "${name}"`);
        setConfirmAction(null);
    };

    const toggleDefault = async (name: string) => {
        if (storage.default === name) {
            await setDefaultPresetOnServer("");
            const refreshed = await loadAllPresetsFromServer();
            setStorage(refreshed);
            showToast("Usunięto domyślny preset");
        } else {
            await setDefaultPresetOnServer(name);
            const refreshed = await loadAllPresetsFromServer();
            setStorage(refreshed);
            showToast(`Ustawiono "${name}" jako domyślny`);
        }
    };

    return (
        <>
            {/* === FILTER DROPDOWN === */}
            <div className="relative inline-block" ref={dropdownRef}>
                <Button variant="outline" size="sm" onClick={() => setDropdownOpen(o => !o)}
                        className="flex items-center gap-2">
                    <Filter className="w-5 h-5"/>
                </Button>

                {dropdownOpen && (
                    <div className="absolute top-full right-0 mt-2 flex bg-white border rounded shadow-lg z-50">
                        <Button variant="ghost" size="icon" onClick={openSaveModal} className="p-2 hover:bg-gray-100 flex items-center">
                            <Save className="w-4 h-4 mr-1"/>
                        </Button>
                        <Button variant="ghost" size="icon" onClick={openLoadModal} className="p-2 hover:bg-gray-100 flex items-center">
                            <Upload className="w-4 h-4 mr-1"/>
                        </Button>
                    </div>
                )}
            </div>

            {/* === MODAL === */}
            {modalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
                    <TooltipProvider>
                        <div ref={modalRef}
                             className="relative bg-white rounded-lg shadow-xl p-6 min-w-[300px] w-[25vw] min-h-[20vh] max-h-[60vh] overflow-y-auto">

                            <Button variant="ghost" size="icon" onClick={closeModal}
                                    className="absolute top-3 right-3 text-gray-500 hover:text-black hover:scale-110 transition">
                                <X className="w-5 h-5"/>
                            </Button>

                            {/* SAVE MODE */}
                            {modalMode === "save" && (
                                <>
                                    <h2 className="text-lg font-semibold mb-4">Zapisz preset</h2>
                                    <Input
                                        ref={inputRef}
                                        type="text"
                                        value={presetName}
                                        onChange={(e) => {
                                            setPresetName(e.target.value);
                                            setValidation(null);
                                        }}
                                        onKeyDown={(e) => e.key === "Enter" && handleSavePreset()}
                                        className="w-full border rounded px-3 py-2 mb-4"
                                        placeholder="Nazwa presetu.."
                                        maxLength={PRESET_NAME_MAX_LENGTH}
                                    />
                                    {validation && (
                                        <div
                                            className={`flex items-start gap-2 rounded-md border px-3 py-2 text-sm mb-4 ${
                                                validation.type === "error"
                                                    ? "border-red-200 bg-red-50 text-red-700"
                                                    : "border-green-200 bg-green-50 text-green-700"
                                            }`}
                                        >
                                            {validation.type === "error" ? (
                                                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0"/>
                                            ) : (
                                                <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0"/>
                                            )}
                                            <span>{validation.message}</span>
                                        </div>
                                    )}
                                    <div className="flex gap-2">
                                        <Button onClick={handleSavePreset} className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <Save className="w-5 h-5"/>
                                                <span>Zapisz</span>
                                            </div>
                                        </Button>
                                        <Button variant="outline" onClick={closeModal}>Anuluj</Button>
                                    </div>
                                </>
                            )}

                            {/* LOAD MODE */}
                            {modalMode === "load" && (
                                <>
                                    <h2 className="text-lg font-semibold mb-4">Twoje presety</h2>

                                    <div className="space-y-2 relative min-h-[150px]">
                                        {Object.keys(storage.presets ?? {}).length === 0 && (
                                            <p className="text-gray-500">Brak zapisanych presetów.</p>
                                        )}

                                        {Object.entries(storage.presets ?? {}).map(([name]) => (
                                            <div key={name}
                                                 className="flex items-center justify-between border p-2 rounded hover:bg-gray-50">

                                                <IconButtonTooltip
                                                    title="Wczytaj preset"
                                                    onClick={() => applyPreset(name)}
                                                    className="flex items-center gap-2 cursor-pointer p-0"
                                                >
                                                    <Upload className="w-5 h-5"/>
                                                    <span className="font-medium truncate">{name}</span>
                                                </IconButtonTooltip>

                                                <div className="flex items-center gap-2">
                                                    <IconButtonTooltip
                                                        title={storage.default === name ? "Domyślny preset" : "Ustaw jako domyślny"}
                                                        onClick={() => toggleDefault(name)}
                                                        className="text-yellow-500"
                                                    >
                                                        {storage.default === name ? (
                                                            <Star className="w-5 h-5 fill-yellow-500"/>
                                                        ) : (
                                                            <Star className="w-5 h-5"/>
                                                        )}
                                                    </IconButtonTooltip>

                                                    <IconButtonTooltip
                                                        title="Usuń preset"
                                                        onClick={() => setConfirmAction({type: "delete", name})}
                                                        className="text-red-500 hover:text-red-700"
                                                    >
                                                        <Trash2 className="w-5 h-5"/>
                                                    </IconButtonTooltip>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </TooltipProvider>
                </div>
            )}

            {confirmAction?.type === "delete" && (
                <AlertPopup
                    type="delete-confirm"
                    title="Usunięcie presetu"
                    description={
                        <>
                            Czy na pewno chcesz usunąć preset{" "}
                            <span className="font-semibold">{confirmAction.name}</span>?
                        </>
                    }
                    onConfirm={() => handleDeletePresetConfirmed(confirmAction.name)}
                    onClose={() => setConfirmAction(null)}
                />
            )}

            {confirmAction?.type === "overwrite" && (
                <AlertPopup
                    type="confirm"
                    title="Nadpisanie presetu"
                    description={
                        <>
                            Preset o nazwie{" "}
                            <span className="font-semibold">{confirmAction.name}</span> już istnieje.
                            Czy chcesz go nadpisać?
                        </>
                    }
                    onConfirm={() => savePresetConfirmed(confirmAction.name, true)}
                    onClose={() => setConfirmAction(null)}
                />
            )}

            {/* === TOAST === */}
            <div className="fixed inset-x-0 top-4 flex items-start justify-center pointer-events-none z-60">
                <div
                    className={`pointer-events-auto transform transition-all duration-300 ease-in-out ${
                        toastVisible ? "translate-y-0 opacity-100" : "-translate-y-6 opacity-0"
                    }`}
                >
                    {toast && (
                        <Toast
                            message={toast}
                            type="success"
                            onClose={() => {
                                setToastVisible(false);
                                setTimeout(() => setToast(null), 300);
                            }}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default FilterPresetButton;